import {
  action, computed, makeAutoObservable, observable, observe, when,
} from 'mobx';
import { PopulatedCampaignUserDtoStatusEnum } from '@coretava/api/ecommerce-service';
import debounce from 'lodash/debounce';
import { getDiffInMinutes } from '@gamiphy/utils/date';
import { SdkStore } from '../sdk';
import { CashbackPopups } from './enums';
import { AppStore } from '../app';
import { ThemeStore } from '../theme';
import { AppCashbackStore } from '../appCashback';
import { CampaignUserStore } from '../campaignUsers';
import { UserStore } from '../user';
import { AppWelcomeGiftStore } from '../appWelcomeGift';
import { DiscountsStore } from '../discounts';
import { SessionStore } from '../session';
import { UiStore } from '../ui';

class CashbackPopupsStore {
  private static USER_INACTIVE_MINUTES_TO_REOPEN_POPUPS = 30;

  @observable cashbackPopupOpen = false;

  @observable cashbackPopupDismissed = false;

  constructor() {
    makeAutoObservable(this);

    void this.addCartUpdatedListener();

    this.watchUserActivity();

    observe(this, 'showCashbackPopups', () => {
      if (this.showCashbackPopups) {
        this.markAsOpened(this.currentActivePopup);
      }
    });
  }

  @computed
  private get cannotShowCashbackPopup(): boolean {
    // Don't show if the user is logged in, and he has no available discount neither enabled cashback
    if (UserStore.isLoggedIn) {
      return !DiscountsStore.bestFitDiscount?.applicableDiscount.discount && !AppCashbackStore.currentAppCashback?.enabled;
    }
    // Don't show if the user is not logged in and welcome gift is disabled and the cashback is disabled
    return !AppWelcomeGiftStore.welcomeGift?.enabled && !AppCashbackStore.currentAppCashback?.enabled;
  }

  @computed get showCashbackPopups(): boolean {
    return this.cashbackPopupOpen &&
    AppStore.isCashbackProgram &&
    !this.isPopupDismissed &&
    !ThemeStore.cashbackPopupsHidden &&
    AppCashbackStore.shouldShowCashbackPopup;
  }

  @computed get showSpinTheWheelPopup(): boolean {
    return !!ThemeStore.theme?.popupsConfig?.spinTheWheel?.enabled &&
      [PopulatedCampaignUserDtoStatusEnum.Pending, PopulatedCampaignUserDtoStatusEnum.Valid]
        .some((status) => status === CampaignUserStore.spinTheWheelCampaignUser?.status);
  }

  @computed get showBogoCampaignPopup(): boolean {
    return [PopulatedCampaignUserDtoStatusEnum.Pending, PopulatedCampaignUserDtoStatusEnum.Valid]
      .some((status) => status === CampaignUserStore.bogoCampaignUser?.status);
  }

  @computed get currentActivePopup(): CashbackPopups {
    if (!UserStore.isLoggedIn) {
      switch (true) {
        case AppWelcomeGiftStore.welcomeGift?.enabled:
          return CashbackPopups.WelcomeGift;
        case this.showSpinTheWheelPopup:
          return CashbackPopups.SpinTheWheel;
        case this.showBogoCampaignPopup:
          return CashbackPopups.Bogo;
        default:
          return CashbackPopups.Cashback;
      }
    } else if (!UserStore.user?.phone) {
      switch (true) {
        case AppWelcomeGiftStore.welcomeGift?.enabled && !DiscountsStore.isWelcomeGiftDiscountValid:
          return CashbackPopups.WelcomeGift;
        case this.showSpinTheWheelPopup:
          return CashbackPopups.SpinTheWheel;
        case this.showBogoCampaignPopup:
          return CashbackPopups.Bogo;
        default:
          return CashbackPopups.Cashback;
      }
    } else if (UserStore.user?.phone) {
      switch (true) {
        case AppWelcomeGiftStore.welcomeGift?.enabled && DiscountsStore.isWelcomeGiftDiscountValid:
          return CashbackPopups.WelcomeGift;
        case this.showSpinTheWheelPopup:
          return CashbackPopups.SpinTheWheel;
        case this.showBogoCampaignPopup:
          return CashbackPopups.Bogo;
        default:
          return CashbackPopups.Cashback;
      }
    }
    return CashbackPopups.Cashback;
  }

  @action openCashbackPopup(): void {
    if (!this.hasPopupOpened(this.currentActivePopup) && !this.cannotShowCashbackPopup) {
      this.cashbackPopupOpen = true;
    }
  }

  @action closeCashbackPopup(): void {
    this.cashbackPopupOpen = false;
    if (this.cashbackPopupDismissed) {
      this.markAsDismissed(this.currentActivePopup);
    }
  }

 @action
  watchUserActivity(): void {
    const debounceLastUserMovement = debounce(() => {
      this.handlePopupsVisiblityOnUserActivity();
      this.handleWelcomeGiftNotificationActivity();
      this.setLastUserActivityTime();
    }, 300);

    window.addEventListener('mousemove', debounceLastUserMovement);
    window.addEventListener('click', debounceLastUserMovement);
    window.addEventListener('keydown', debounceLastUserMovement);
    window.addEventListener('scroll', debounceLastUserMovement);
  }

  @action
 setLastUserActivityTime(): void {
   SessionStore.updateSession({ lastActivityTime: new Date().toISOString() });
 }

 @action
  handleWelcomeGiftNotificationActivity(): void {
    const lastUserMovementTime = SessionStore.coretavaSession?.lastActivityTime;
    if (lastUserMovementTime && getDiffInMinutes(new Date(), new Date(lastUserMovementTime)) > CashbackPopupsStore.USER_INACTIVE_MINUTES_TO_REOPEN_POPUPS) {
      SessionStore.updateSession({ welcomeGiftNotification: { opened: false } });
      UiStore.openWelcomeGiftNotification();
    }
  }

  @action
 handlePopupsVisiblityOnUserActivity(): void {
   const lastUserMovementTime = SessionStore.coretavaSession?.lastActivityTime;
   if (lastUserMovementTime && getDiffInMinutes(new Date(), new Date(lastUserMovementTime)) > CashbackPopupsStore.USER_INACTIVE_MINUTES_TO_REOPEN_POPUPS) {
     switch (this.currentActivePopup) {
       case CashbackPopups.WelcomeGift:
         SessionStore.updatePopup(CashbackPopups.WelcomeGift, { opened: false });
         break;
       case CashbackPopups.SpinTheWheel:
         SessionStore.updatePopup(CashbackPopups.SpinTheWheel, { opened: false });
         break;
       case CashbackPopups.Bogo:
         SessionStore.updatePopup(CashbackPopups.Bogo, { opened: false });
         break;
       case CashbackPopups.Cashback:
         SessionStore.updatePopup(CashbackPopups.Cashback, { opened: false });
         break;
       default:
     }
   }
 }

  private markAsOpened(popup: CashbackPopups): void {
    switch (popup) {
      case CashbackPopups.WelcomeGift:
        SessionStore.updatePopup(CashbackPopups.WelcomeGift, { opened: true });
        break;
      case CashbackPopups.SpinTheWheel:
        SessionStore.updatePopup(CashbackPopups.SpinTheWheel, { opened: true });
        break;
      case CashbackPopups.Bogo:
        SessionStore.updatePopup(CashbackPopups.Bogo, { opened: true });
        break;
      case CashbackPopups.Cashback:
        SessionStore.updatePopup(CashbackPopups.Cashback, { opened: true });
        break;
      default:
    }
  }

  private markAsDismissed(popup: CashbackPopups): void {
    switch (popup) {
      case CashbackPopups.WelcomeGift:
        SessionStore.updatePopup(CashbackPopups.WelcomeGift, { dismissed: true });
        break;
      case CashbackPopups.SpinTheWheel:
        SessionStore.updatePopup(CashbackPopups.SpinTheWheel, { dismissed: true });
        break;
      case CashbackPopups.Bogo:
        SessionStore.updatePopup(CashbackPopups.Bogo, { dismissed: true });
        break;
      case CashbackPopups.Cashback:
        SessionStore.updatePopup(CashbackPopups.Cashback, { dismissed: true });
        break;
      default:
    }
  }

  @computed get isPopupDismissed(): boolean {
    switch (this.currentActivePopup) {
      case CashbackPopups.WelcomeGift:
        return !!SessionStore.coretavaSession?.popups?.WelcomeGift?.dismissed;
      case CashbackPopups.SpinTheWheel:
        return !!SessionStore.coretavaSession?.popups?.SpinTheWheel?.dismissed;
      case CashbackPopups.Bogo:
        return !!SessionStore.coretavaSession?.popups?.bogo?.dismissed;
      case CashbackPopups.Cashback:
        return !!SessionStore.coretavaSession?.popups?.Cashback?.dismissed;
      default:
        return false;
    }
  }

  private hasPopupOpened(popup: CashbackPopups): boolean {
    switch (popup) {
      case CashbackPopups.WelcomeGift:
        return !!SessionStore.coretavaSession?.popups?.WelcomeGift?.opened;
      case CashbackPopups.SpinTheWheel:
        return !!SessionStore.coretavaSession?.popups?.SpinTheWheel?.opened;
      case CashbackPopups.Bogo:
        return !!SessionStore.coretavaSession?.popups?.bogo?.opened;
      case CashbackPopups.Cashback:
        return !!SessionStore.coretavaSession?.popups?.Cashback?.opened;
      default:
        return false;
    }
  }

  @action setCashbackPopupDismissed(dismissed: boolean): void {
    this.cashbackPopupDismissed = dismissed;
  }

  @action
  async addCartUpdatedListener(): Promise<void> {
    await when(() => !!SdkStore.cart);

    SdkStore.cart?.addCartUpdatedListener(() => {
      this.openCashbackPopup();
    });
  }
}

export default new CashbackPopupsStore();
